import React, { useState, useCallback } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faHome, faCog, faDesktop, faUser, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../hooks/useAuth';
import { Header } from '../ui/Header';
import { Footer } from '../ui/Footer';
import { SearchSpotlight } from '../common/SearchSpotlight';
import { ToastContainer } from '../common/ToastContainer';
import logoWiki from '../../assets/icons/logo-wiki.svg';
import styles from './WikiLayout.module.css';

const NAV_ITEMS = [
  { to: '/', label: 'Dashboard', slug: 'dashboard', icon: faHome, show: true },
  { to: '/infra', label: 'Infraestructura', slug: 'infraestructura', icon: faDesktop, show: true },
  { to: '/profile', label: 'Mi perfil', slug: 'mi-perfil', icon: faUser, show: true },
  { to: '/admin', label: 'Administración', slug: 'administracion', icon: faCog, show: true },
];

export const WikiLayout: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleToggle = useCallback(() => {
    setCollapsed((v) => !v);
  }, []);

  const roles: string[] = Array.isArray(user?.roles) ? user.roles : [];
  const isAdmin = roles.some(r => ['ROOT', 'ADMIN', 'IT_ADMIN'].includes(r.toUpperCase()));
  const isSystemAdmin = roles.some(r => r.toUpperCase() === 'IT_ADMIN');

  const visibleItems = NAV_ITEMS.filter(item => {
    if (item.to === '/admin') return isAdmin;
    if (item.to === '/infra') return isSystemAdmin;
    return item.show;
  });

  const nav = (
    <nav className={styles['layout__nav']}>
      <span className={styles['layout__section']}>Navegación</span>
      {visibleItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          onClick={() => setDrawerOpen(false)}
          data-testid={`sidebar-nav-${item.slug}`}
          className={({ isActive }) =>
            [
              styles['layout__link'],
              isActive ? styles['layout__link--active'] : '',
            ]
              .filter(Boolean)
              .join(' ')
          }
        >
          <FontAwesomeIcon icon={item.icon} className={styles['layout__link-icon']} />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className={styles['layout']}>
      <aside
        data-testid="sidebar"
        className={`${styles['layout__sidebar']} ${collapsed ? styles['layout__sidebar--collapsed'] : ''}`}
      >
        <div className={styles['layout__header']}>
          {!collapsed && (
            <img src={logoWiki} alt="Wiki" className={styles['layout__logo']} />
          )}
          <button
            type="button"
            className={styles['layout__toggle']}
            onClick={handleToggle}
            aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            <FontAwesomeIcon icon={collapsed ? faChevronRight : faChevronLeft} />
          </button>
        </div>
        {nav}
      </aside>

      {drawerOpen && (
        <div className={styles['layout__overlay']} onClick={() => setDrawerOpen(false)} />
      )}
      <aside
        className={[
          styles['layout__drawer'],
          drawerOpen ? styles['layout__drawer--open'] : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className={styles['layout__drawer-header']}>
          <img src={logoWiki} alt="Wiki" className={styles['layout__logo']} />
          <button
            className={styles['layout__close-btn']}
            type="button"
            onClick={() => setDrawerOpen(false)}
            aria-label="Cerrar menu"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        {nav}
      </aside>

      <div className={styles['layout__main']}>
        <Header onMenuToggle={() => setDrawerOpen(true)} />
        <main key={location.pathname} className={styles['layout__content']}>
          <Outlet />
        </main>
        <Footer />
      </div>
      <ToastContainer />
      <SearchSpotlight />
    </div>
  );
};
